// src/stores/trackerStore.ts
import { create } from 'zustand';
import { TrackerEntry, PickStatus, ParlayStatus } from '../domain/types';
import { indexedDBRepo } from '../services/trackerRepo';

type FilterTab = 'all' | '7days' | '30days' | 'today';

interface TrackerState {
  entries: TrackerEntry[];
  filter: FilterTab;
  isLoading: boolean;

  setFilter: (filter: FilterTab) => void;
  load: () => Promise<void>;
  removeEntry: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;

  // getters
  filteredEntries: TrackerEntry[];
  stats: {
    totalPicks: number;
    won: number;
    lost: number;
    push: number;
    totalParlays: number;
    green: number;
    red: number;
    void: number;
    roi: number;
  };
}

const statusOrder: Record<string, number> = {
  [PickStatus.PENDING]: 0,
  [PickStatus.WON]: 1,
  [PickStatus.LOST]: 2,
  [PickStatus.PUSH]: 3,
  [PickStatus.VOID]: 3,
  [ParlayStatus.GREEN]: 1,
  [ParlayStatus.RED]: 2,
};

export const useTrackerStore = create<TrackerState>((set, get) => ({
  entries: [],
  filter: 'all',
  isLoading: false,

  setFilter: (filter) => set({ filter }),

  load: async () => {
    set({ isLoading: true });
    const entries = await indexedDBRepo.getAll();
    // ordena: pendentes primeiro, depois por data decrescente
    entries.sort((a, b) => {
      const sa = a.type === 'parlay' ? a.data.status : a.data.status;
      const sb = b.type === 'parlay' ? b.data.status : b.data.status;
      const oa = statusOrder[sa] ?? 99;
      const ob = statusOrder[sb] ?? 99;
      if (oa !== ob) return oa - ob;
      const da = new Date(a.data.createdAt ?? (a.type === 'pick' ? a.data.startISO : '')).getTime();
      const db = new Date(b.data.createdAt ?? (b.type === 'pick' ? b.data.startISO : '')).getTime();
      return db - da;
    });
    set({ entries, isLoading: false });
  },

  removeEntry: async (id) => {
    await indexedDBRepo.remove(id);
    set({ entries: get().entries.filter(e => e.data.id !== id) });
  },

  clearAll: async () => {
    await indexedDBRepo.clear();
    set({ entries: [] });
  },

  get filteredEntries() {
    const { entries, filter } = get();
    if (filter === 'all') return entries;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const since = new Date(today);
    if (filter === '7days') since.setDate(since.getDate() - 6);
    if (filter === '30days') since.setDate(since.getDate() - 29);
    return entries.filter(entry => {
      if (entry.type === 'parlay') return true;
      const rawDate = entry.data.startISO || entry.data.createdAt;
      if (!rawDate) return false;
      const date = new Date(rawDate); date.setHours(0, 0, 0, 0);
      return filter === 'today' ? date.getTime() === today.getTime() : date >= since;
    });
  },

  get stats() {
    const { entries } = get();
    const picks = entries.filter(e => e.type === 'pick');
    const parlays = entries.filter(e => e.type === 'parlay');

    const won = picks.filter(p => p.data.status === PickStatus.WON).length;
    const lost = picks.filter(p => p.data.status === PickStatus.LOST).length;
    const push = picks.filter(p => p.data.status === PickStatus.PUSH).length;
    const green = parlays.filter(p => p.data.status === ParlayStatus.GREEN).length;
    const red = parlays.filter(p => p.data.status === ParlayStatus.RED).length;
    const voidP = parlays.filter(p => p.data.status === ParlayStatus.VOID).length;

    const totalStaked = entries.reduce((sum, e) =>
      sum + (e.type === 'pick' ? e.data.stake : e.data.stake), 0);
    const totalReturned = entries.reduce((sum, e) => {
      if (e.type === 'pick') {
        if (e.data.status === PickStatus.WON) return sum + e.data.stake * e.data.odds;
        if (e.data.status === PickStatus.PUSH) return sum + e.data.stake;
        return sum;
      }
      if (e.data.status === ParlayStatus.GREEN) return sum + e.data.potentialReturn;
      if (e.data.status === ParlayStatus.VOID) return sum + e.data.stake;
      return sum;
    }, 0);

    const roi = totalStaked > 0 ? ((totalReturned - totalStaked) / totalStaked) * 100 : 0;

    return {
      totalPicks: picks.length,
      won,
      lost,
      push,
      totalParlays: parlays.length,
      green,
      red,
      void: voidP,
      roi: Math.round(roi * 100) / 100,
    };
  },
}));
